import { DataStreamBaseTileConfig, ShapeName, StreamDataColumnDefinition } from '@squaredup/data-streams';
import LoadingSpinner from 'components/LoadingSpinner';
import { useDataStreamConfig } from 'dashboard-engine/hooks/useDataStreamConfig';
import { PartialDeep } from 'type-fest';
import { sampleValueFrom } from '../MetadataEditorState';
import { FormattedValue } from './FormattedValue';
import { RawValue } from './RawValue';

interface ValuePreviewProps {
    config: DataStreamBaseTileConfig;
    columnName: string;
    columnShape: ShapeName;
    metadata: PartialDeep<StreamDataColumnDefinition>[];
}

export const PreviewSampleValue: React.FC<ValuePreviewProps> = ({ config, columnName, columnShape, metadata }) => {
    const { data, isLoading, isFetching } = useDataStreamConfig(
        {
            ...config,
            dataStream: {
                ...config.dataStream,
                metadata
            }
        },
        { keepPreviousData: true }
    );

    const getSampleValue = sampleValueFrom(data.rows, data.metadata.columns);
    const sampleValue = getSampleValue(columnName);

    if (isLoading) {
        return (
            <div className='flex items-center px-8 py-4 mb-6 bg-dividerTertiary border-y border-dividerTertiary h-[72px]'>
                <LoadingSpinner size={16} />
            </div>
        );
    }

    return (
        <dl className='grid w-full grid-cols-2 px-8 py-4 mb-6 divide-x bg-dividerTertiary divide-dividerSecondary text-textPrimary'>
            <div>
                <dt className='font-semibold'>Raw</dt>
                <dd className='font-light mt-0.5' data-testid='rawValue'>
                    <RawValue rawValue={sampleValue.raw} />
                </dd>
            </div>
            <div className='pl-6'>
                <dt className='font-semibold'>Formatted</dt>
                <dd className='font-light mt-0.5' data-testid='formattedValue'>
                    <FormattedValue
                        shapeName={columnShape}
                        sampleValue={sampleValue}
                        isLoading={isFetching || isLoading}
                    />
                </dd>
            </div>
        </dl>
    );
};
